// User Types
export interface User {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber?: string;
  userType: UserType;
  status: UserStatus;
  dateOfBirth?: string;
  schoolName?: string;
  gradeLevel?: string;
  profilePictureUrl?: string;
  emailVerified: boolean;
  phoneVerified: boolean;
  createdAt: string;
  updatedAt: string;
}

export enum UserType {
  STUDENT = 'STUDENT',
  PARENT = 'PARENT',
  TEACHER = 'TEACHER',
  TUTOR = 'TUTOR',
  SYSTEM_ADMIN = 'SYSTEM_ADMIN',
  SCHOOL_ADMIN = 'SCHOOL_ADMIN',
}

export enum UserStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  PENDING = 'PENDING',
  SUSPENDED = 'SUSPENDED',
  DELETED = 'DELETED',
}

// Course Types
export interface Course {
  id: number;
  name: string;
  description: string;
  courseType: CourseType;
  status: CourseStatus;
  maxStudents: number;
  durationWeeks: number;
  difficultyLevel: string;
  prerequisites?: string;
  curriculumAlignment?: string;
  createdAt: string;
  updatedAt: string;
  /** OPEN, CODE, or INVITE — from core-service courses.registration_method */
  registrationMethod?: string;
}

export enum CourseType {
  SECONDARY_SCHOOL = 'SECONDARY_SCHOOL',
  PRIMARY_SCHOOL = 'PRIMARY_SCHOOL',
  ABACUS_MATHS = 'ABACUS_MATHS',
  PIANO = 'PIANO',
  CHESS = 'CHESS',
  MUSIC = 'MUSIC',
  ART = 'ART',
}

export enum CourseStatus {
  ACTIVE = 'ACTIVE',
  COMING_SOON = 'COMING_SOON',
  INACTIVE = 'INACTIVE',
  ARCHIVED = 'ARCHIVED',
}

// Course Code Types
export interface CourseCode {
  id: number;
  code: string;
  course: Course;
  createdBy: User;
  codeType: CodeType;
  maxUses?: number;
  currentUses: number;
  expiresAt?: string;
  status: CodeStatus;
  targetEmails?: string;
  createdAt: string;
  updatedAt: string;
}

export enum CodeType {
  INDIVIDUAL = 'INDIVIDUAL',
  CLASS = 'CLASS',
  SCHOOL = 'SCHOOL',
}

export enum CodeStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  EXPIRED = 'EXPIRED',
  DEPLETED = 'DEPLETED',
}

// Enrollment Types
export interface CourseEnrollment {
  id: number;
  user: User;
  course: Course;
  courseCode?: CourseCode;
  status: EnrollmentStatus;
  enrolledAt: string;
  completedAt?: string;
  progressPercentage?: number;
  updatedAt: string;
  enrolledBy?: string;
}

export enum EnrollmentStatus {
  ENROLLED = 'ENROLLED',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  DROPPED = 'DROPPED',
  SUSPENDED = 'SUSPENDED',
}

// DTOs
export interface UserRegistrationRequest {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  phoneNumber?: string;
  userType: UserType;
  dateOfBirth?: string;
  schoolName?: string;
  gradeLevel?: string;
}

// Child-related types moved to types/child.ts

export interface CourseCodeGenerationRequest {
  courseId: number;
  codeType: CodeType;
  maxUses?: number;
  expiresAt?: string;
  targetEmails?: string[];
  createdByUserId: number;
}

export interface CourseCodeRegistrationRequest {
  courseCode: string;
  userId: number;
  enrolledBy?: string;
}

// API Response Types
export interface ApiResponse<T> {
  success: boolean;
  message: string;
  data?: T;
  timestamp: string;
  errorCode?: string;
}

// Form Types
export interface LoginFormData {
  email: string;
  password: string;
}

export interface SignUpFormData {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  confirmPassword: string;
  phoneNumber?: string;
  userType: UserType;
  dateOfBirth?: string;
  schoolName?: string;
  gradeLevel?: string;
}

// Role Identity Types (Spec 020)
export interface UserRoleProfile {
  roleType: string;
  phoneNumber?: string;
  schoolCountry?: string;
  schoolCouncil?: string;
  schoolName?: string;
  schoolType?: string;
  courseInterests?: string;
  activatedAt?: string;
  updatedAt?: string;
}

export interface ActivatedRole {
  roleType: string;
  status: 'ACTIVE' | 'AVAILABLE' | 'COMING_SOON';
}

export interface ActivateRoleRequest {
  phoneNumber?: string;
  schoolCountry?: string;
  schoolCouncil?: string;
  schoolName?: string;
  schoolType?: string;
  courseInterests?: string;
}

export interface RoleAvailability {
  activatedRoles: UserRoleProfile[];
  availableRoles: ActivatedRole[];
}

// UI Types
export interface LoadingState {
  loading: boolean;
  error: string | null;
}

export interface PaginationParams {
  page: number;
  size: number;
  sort?: string;
}

export interface SearchFilters {
  searchTerm?: string;
  courseType?: CourseType;
  status?: CourseStatus;
  difficultyLevel?: string;
}
